import BaseApi, { Response, PagedResponse } from './baseApi';
import { BaseUser, CreateUser, UpdateUser } from '../dtos/Users/index';

// Re-export DTOs for convenience
export type { BaseUser, CreateUser, UpdateUser };

class UserApi extends BaseApi {
  private readonly basePath = '/users';

  /**
   * Get user by ID
   * GET /api/users/{id}
   */
  async getById(id: string): Promise<Response<BaseUser | null>> {
    return this.get<BaseUser | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all users
   * GET /api/users
   */
  async getList(): Promise<Response<BaseUser[]>> {
    return this.get<BaseUser[]>(this.basePath);
  }

  /**
   * Get paginated list of users with optional search
   * GET /api/users/search?searchTerm=&pageNumber=1&pageSize=10 (when search is provided)
   * GET /api/users/paged?pageNumber=1&pageSize=10 (when no search)
   * Searches: FirstName, LastName, Email, Phone
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BaseUser>>> {
    // Use /search endpoint when searchTerm is provided, otherwise use /paged
    if (searchTerm) {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      return this.get<PagedResponse<BaseUser>>(
        `${this.basePath}/search?${params.toString()}`
      );
    }
    
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BaseUser>>(
      `${this.basePath}/paged?${params.toString()}`
    );
  }

  /**
   * Create a new user
   * POST /api/users
   */
  async create(userData: CreateUser): Promise<Response<BaseUser>> {
    return this.post<BaseUser, CreateUser>(this.basePath, userData);
  }

  /**
   * Update an existing user
   * PUT /api/users/{id}
   */
  async update(id: string, userData: Omit<UpdateUser, 'id'>): Promise<Response<BaseUser>> {
    return this.put<BaseUser, UpdateUser>(`${this.basePath}/${id}`, { ...userData, id });
  }

  /**
   * Delete a user
   * DELETE /api/users/{id}
   */
  async deleteUser(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const userApi = new UserApi();

export default UserApi;
